import { getQueryKey } from "@core/hooks/useSession";
import type { Page, PageProps } from "@core/types";
import getServerSession from "@core/utils/getServerSession";
import {
  fetchSubtypes,
  getQueryKey as subtypeQueryKey,
} from "@features/community-v2/hooks/useSubtypes";
import SubtypeFeedScreen from "@features/community-v2/screens/SubtypeFeedScreen";
import { GetServerSideProps } from "next";
import { NextSeo } from "next-seo";
import React from "react";
import { QueryClient } from "react-query";
import { dehydrate } from "react-query/hydration";

interface Props extends PageProps {
  segmentId: string | null;
  subtypeId: string;
  title: string;
  slug: string;
}

const SubtypeFeedPage: Page<Props> = ({
  segmentId,
  subtypeId,
  title,
  slug,
}) => {
  const metaTitle = `${title} Forum | ${title} Community`;
  const metaDescription = `The NOCD Community provides support for people with ${title}. You can meet others, share your experiences, and learn from others on a similar journey with ${title}.`;

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          description: metaDescription,
          title: metaTitle,
        }}
        canonical={`${process.env.NEXT_PUBLIC_CANONICAL_URL}/community/subtypes/${slug}`}
      />
      <SubtypeFeedScreen
        subtypeSlug={slug}
        segmentId={segmentId}
        subtypeId={subtypeId}
        title={title}
      />
    </>
  );
};

export const getServerSideProps: GetServerSideProps<
  unknown,
  { slug: string; segmentId?: string }
> = async (ctx) => {
  const queryClient = new QueryClient();

  const session = await getServerSession(ctx);
  // Pre-load the session query
  queryClient.setQueryData(getQueryKey(), session);

  const { slug } = ctx.params;
  let { segmentId } = ctx.query;

  if (Array.isArray(segmentId)) {
    [segmentId] = segmentId;
  }

  const subtypes = await fetchSubtypes();

  queryClient.setQueryData(subtypeQueryKey(), subtypes);

  const subtype = subtypes.find((item) => item.slug === slug);

  if (!subtype) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      slug,
      segmentId: segmentId ?? null,
      subtypeId: subtype.id,
      title: subtype.title,
    },
  };
};

// The page is publicly accessible, so we want to show the OneTrust consent
// dialog to the user if they are not logged in.
SubtypeFeedPage.shouldShowConsentDialog = true;
SubtypeFeedPage.backgroundColor = "gray-v2";

export default SubtypeFeedPage;
